import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from '@/assets/icons';
import { Button } from '@/components/ui/button';
import { isUnproofread } from '@/i18n/languages';

const CLOSED_KEY_PREFIX = 'stitchpress-unproofread-notice-closed:';

/** Storage can throw (private window, blocked site data) — treat that as "not closed". */
function wasClosed(language: string): boolean {
    try {
        return localStorage.getItem(CLOSED_KEY_PREFIX + language) === '1';
    } catch {
        return false;
    }
}

function rememberClosed(language: string): void {
    try {
        localStorage.setItem(CLOSED_KEY_PREFIX + language, '1');
    } catch {
        // Not persisted; component state still keeps it closed for this page load.
    }
}

interface UnproofreadTranslationNoticeProps {
    /**
     * The live UI language (`i18n.language`). A prop rather than read here so
     * the root, which already re-renders on language change, drives it.
     */
    language: string;
}

/**
 * Sticky top-center notice that the active UI language's translations haven't
 * been proofread (see `UNPROOFREAD_LANGUAGES`). It stays until the user closes
 * it, and the close is remembered per language in localStorage — so it carries
 * across the full-page navigations into the editors (`reloadDocument` links)
 * that would wipe an in-memory sonner toast, and it doesn't come back after
 * switching away and back. Keyed off the live language, it also appears during
 * the Settings language preview (before Apply) and leaves if that's discarded.
 *
 * Placement: `top-2` floats it over the empty middle of the editors' header
 * tray, clear of the toolbar row and mode toggle below; `max-w-xl` keeps it
 * off the tray's name/save cluster on the left.
 */
export function UnproofreadTranslationNotice({ language }: UnproofreadTranslationNoticeProps) {
    const { t } = useTranslation();
    const [closedHere, setClosedHere] = useState<ReadonlySet<string>>(() => new Set());

    if (!isUnproofread(language) || closedHere.has(language) || wasClosed(language)) return null;

    const close = () => {
        rememberClosed(language);
        setClosedHere((prev) => new Set(prev).add(language));
    };

    return (
        <div
            role="status"
            className="pointer-events-auto fixed top-2 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 items-start gap-2 rounded-lg border border-border bg-background/95 py-2 pr-2 pl-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80"
        >
            <p className="flex-1 py-1.5 text-sm">{t('unproofreadTranslationNotice.message')}</p>
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('unproofreadTranslationNotice.close')}
                onClick={close}
                className="shrink-0"
            >
                <X className="size-4" />
            </Button>
        </div>
    );
}
